import { ImageUploader } from '../../components/ImageUploader';
import { Menu, MenuItem, MenuLabel } from '../../components/ui';
import {
  CHOICE_TYPES,
  QUESTION_TYPES,
  QUESTION_TYPE_HINTS,
  QUESTION_TYPE_LABELS,
  type Question,
  type QuestionType,
  type Section,
} from '../../lib/types';
import { cx, moveItem, newId } from '../../lib/utils';
import { QuestionEditor } from './QuestionEditor';

interface Props {
  section: Section;
  index: number;
  total: number;
  firstQuestionNumber: number;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  expandedIds: ReadonlySet<string>;
  onToggleQuestion: (id: string, open?: boolean) => void;
  /** Problems to show inline, keyed by section or question id. */
  issues: ReadonlyMap<string, string>;
  otherSections: { id: string; title: string }[];
  onChange: (section: Section) => void;
  onMove: (delta: -1 | 1) => void;
  onAddBelow: () => void;
  onDuplicate: () => void;
  onMoveQuestionTo: (questionIndex: number, sectionId: string) => void;
  onDelete: () => void;
}

export function SectionEditor({
  section,
  index,
  total,
  firstQuestionNumber,
  collapsed,
  onToggleCollapsed,
  expandedIds,
  onToggleQuestion,
  issues,
  otherSections,
  onChange,
  onMove,
  onAddBelow,
  onDuplicate,
  onMoveQuestionTo,
  onDelete,
}: Props) {
  const patch = (p: Partial<Section>) => onChange({ ...section, ...p });
  const setQuestions = (questions: Question[]) => patch({ questions });

  const count = section.questions.length;
  const required = section.questions.filter((q) => q.required).length;
  const titleIssue = issues.get(section.id);
  const hasIssue = Boolean(titleIssue) || section.questions.some((q) => issues.has(q.id));

  function addQuestion(type: QuestionType) {
    // New questions start optional: clients finish forms more often when only essentials are required.
    const q: Question = {
      id: newId('q'),
      type,
      label: '',
      required: false,
      options: CHOICE_TYPES.has(type) ? ['Option 1', 'Option 2'] : undefined,
    };
    setQuestions([...section.questions, q]);
    onToggleQuestion(q.id, true);
  }

  function duplicateQuestion(i: number) {
    const source = section.questions[i]!;
    const copy: Question = {
      ...source,
      id: newId('q'),
      options: source.options && [...source.options],
    };
    const list = [...section.questions];
    list.splice(i + 1, 0, copy);
    setQuestions(list);
    onToggleQuestion(copy.id, true);
  }

  function deleteQuestion(i: number) {
    const q = section.questions[i]!;
    if (q.label && !window.confirm(`Delete the question “${q.label.slice(0, 80)}”?`)) return;
    setQuestions(section.questions.filter((_, j) => j !== i));
  }

  function setAllRequired(value: boolean) {
    setQuestions(section.questions.map((q) => ({ ...q, required: value })));
  }

  const summary = (
    <span className="section-meta">
      {count} {count === 1 ? 'question' : 'questions'}
      {count > 0 && ` · ${required ? `${required} required` : 'all optional'}`}
    </span>
  );

  return (
    <section
      className={cx(
        'card section-editor editor-anchor',
        collapsed && 'collapsed',
        hasIssue && 'has-issue',
      )}
      id={`section-${section.id}`}
    >
      <div className="section-editor-head">
        <button
          type="button"
          className="section-toggle"
          onClick={onToggleCollapsed}
          aria-expanded={!collapsed}
          title={collapsed ? 'Expand section' : 'Collapse section'}
        >
          <span className="chevron" aria-hidden>
            ▾
          </span>
          <span className="section-index">Section {index + 1}</span>
          {summary}
        </button>
        <div className="row tight">
          <button
            type="button"
            className="icon-btn"
            title="Move section up"
            aria-label="Move section up"
            disabled={index === 0}
            onClick={() => onMove(-1)}
          >
            ↑
          </button>
          <button
            type="button"
            className="icon-btn"
            title="Move section down"
            aria-label="Move section down"
            disabled={index === total - 1}
            onClick={() => onMove(1)}
          >
            ↓
          </button>
          <Menu label="Section actions" trigger="⋯">
            <MenuItem onSelect={onAddBelow}>Add a section below</MenuItem>
            <MenuItem onSelect={onDuplicate}>Duplicate section</MenuItem>
            <MenuLabel>Questions in this section</MenuLabel>
            <MenuItem
              onSelect={() => setAllRequired(false)}
              disabled={count === 0 || required === 0}
            >
              Make all optional
            </MenuItem>
            <MenuItem
              onSelect={() => setAllRequired(true)}
              disabled={count === 0 || required === count}
            >
              Make all required
            </MenuItem>
            <MenuItem onSelect={onDelete} danger>
              Delete section
            </MenuItem>
          </Menu>
        </div>
      </div>

      {collapsed ? (
        <button type="button" className="section-collapsed-title" onClick={onToggleCollapsed}>
          {section.title || <em className="muted">Untitled section</em>}
        </button>
      ) : (
        <>
          <input
            id={`section-title-${section.id}`}
            className="input input-title"
            value={section.title}
            onChange={(e) => patch({ title: e.target.value })}
            placeholder="Section title"
            aria-label="Section title"
            aria-invalid={titleIssue ? true : undefined}
          />
          {titleIssue && <p className="error-text section-error">{titleIssue}</p>}
          <textarea
            className="textarea"
            rows={2}
            value={section.description ?? ''}
            onChange={(e) => patch({ description: e.target.value })}
            placeholder="Section introduction (optional) — e.g. what this part covers or how to answer"
            aria-label="Section description"
          />
          <ImageUploader
            compact
            label="Section image"
            value={section.imageId}
            onChange={(imageId) => patch({ imageId })}
          />

          <div className="question-list">
            {section.questions.map((q, i) => (
              <QuestionEditor
                key={q.id}
                question={q}
                number={firstQuestionNumber + i}
                expanded={expandedIds.has(q.id)}
                isFirst={i === 0}
                isLast={i === section.questions.length - 1}
                issue={issues.get(q.id)}
                otherSections={otherSections}
                onToggle={() => onToggleQuestion(q.id)}
                onChange={(p) =>
                  setQuestions(section.questions.map((x, j) => (j === i ? { ...x, ...p } : x)))
                }
                onMove={(d) => setQuestions(moveItem(section.questions, i, i + d))}
                onMoveTo={(sectionId) => onMoveQuestionTo(i, sectionId)}
                onDuplicate={() => duplicateQuestion(i)}
                onDelete={() => deleteQuestion(i)}
              />
            ))}
            {section.questions.length === 0 && (
              <p className="muted small">No questions in this section yet.</p>
            )}
          </div>

          <Menu
            label="Add question"
            trigger="+ Add question"
            triggerClassName="btn btn-secondary btn-sm"
            align="left"
          >
            <MenuLabel>Choose an answer type</MenuLabel>
            <div className="type-grid">
              {QUESTION_TYPES.map((t) => (
                <MenuItem key={t} onSelect={() => addQuestion(t)} hint={QUESTION_TYPE_HINTS[t]}>
                  {QUESTION_TYPE_LABELS[t]}
                </MenuItem>
              ))}
            </div>
          </Menu>
        </>
      )}
    </section>
  );
}
